import { Route, Routes, matchPath, parsePath } from 'react-router-dom';

import { To } from '@remix-run/router';

import { APP_ROUTES, PROTECTED_PATH_PATTERNS, isSandboxBlockedPath } from './appRoutes';
import { routePathPattern } from './appRoutes.constants';
import { PageShellProvider } from './components/PageShell/PageShell';
import { InterceptingBrowserRouter } from './providers/InterceptingBrowserRouter';
import { PaywalledRoute } from './providers/PaywalledRoute';
import { PermissionRestrictedRoute } from './providers/PermissionRestrictedRoute';
import { PostHogPageViewShell } from './providers/PostHogPageViewShell';
import { ProtectedRoute } from './providers/ProtectedRoute';
import { SandboxRoute } from './providers/SandboxRoute';
import { openSignupPrompt } from './redux/appState/appState';
import { store } from './redux/store';
import { IS_SANDBOX } from './utils/sandboxSession';
import { getBackrsToken, isGuestToken } from './utils/tokenHelpers';

const toPathname = (to: To): string =>
  typeof to === 'string' ? (parsePath(to).pathname ?? '') : (to.pathname ?? '');

const shouldBlockNavigation = (to: To): boolean => {
  const pathname = toPathname(to);

  if (IS_SANDBOX) return isSandboxBlockedPath(pathname);

  if (!isGuestToken(getBackrsToken())) return false;

  return PROTECTED_PATH_PATTERNS.some((pattern) => matchPath(pattern, pathname) != null);
};

const openPromptForBlockedNavigation = (): void => {
  store.dispatch(openSignupPrompt(IS_SANDBOX ? 'sandbox' : undefined));
};

export const App = () => {
  return (
    <InterceptingBrowserRouter
      shouldBlock={shouldBlockNavigation}
      onBlocked={openPromptForBlockedNavigation}
    >
      <PostHogPageViewShell>
        <Routes>
          <Route element={<PageShellProvider />}>
            {Object.values(APP_ROUTES).map((route) => {
              const path = routePathPattern(route);

              const baseElement = route.protected ? (
                <ProtectedRoute>
                  {route.redirectIfMissingPermissions ? (
                    <PermissionRestrictedRoute
                      requiredPermissions={route.redirectIfMissingPermissions}
                    >
                      {route.requiredPermissions ? (
                        <PaywalledRoute
                          requiredPermissions={route.requiredPermissions}
                          paymentPageProps={route.paymentPageProps}
                        >
                          {route.page}
                        </PaywalledRoute>
                      ) : (
                        route.page
                      )}
                    </PermissionRestrictedRoute>
                  ) : route.requiredPermissions ? (
                    <PaywalledRoute
                      requiredPermissions={route.requiredPermissions}
                      paymentPageProps={route.paymentPageProps}
                    >
                      {route.page}
                    </PaywalledRoute>
                  ) : (
                    route.page
                  )}
                </ProtectedRoute>
              ) : (
                route.page
              );

              // Navigation is intercepted before it happens; this catches a pasted or bookmarked URL.
              const element = IS_SANDBOX ? <SandboxRoute>{baseElement}</SandboxRoute> : baseElement;

              return (
                <Route key={route.path} path={path} element={element}>
                  {route.subRoute && <Route path={`:${route.subRoute}`} element={element} />}
                </Route>
              );
            })}
          </Route>
        </Routes>
      </PostHogPageViewShell>
    </InterceptingBrowserRouter>
  );
};
